import { createFileRoute, useNavigate, useRouteContext } from "@tanstack/react-router";
import { z } from "zod";
import { Box, Button, Stack, Tabs, Typography } from "@mui/material";
import { PaxErrorPage, PaxTab, PaxTabPanel, PaxTooltip, useTranslation } from "@ncino/pax-ui";
import { PageContentContainer } from "@/shared/components/PageContentContainer";
import { TearSheet } from "@/shared/components/PoolDetails/TearSheet";
import { InstitutionProfile } from "@/shared/components/Institution/InstitutionProfile";
import Divider from "@mui/material/Divider";
import { PoolAnalytics } from "@/shared/components/PoolDetails/PoolAnalytics";
import { PoolLoanList } from "@/shared/components/PoolDetails/PoolLoanList";
import { LetterOfIntentStatus, useGetPoolForBuyPageQuery } from "@/services/graphql/reactQuery";
import { Fragment, useMemo, useState } from "react";
import { LoadingPageComponent } from "@/shared/components/LoadingPage";
import { tabSchema, TabValue } from "./-internal/types";
import { useBuyRouteContext } from "./-internal/BuyRouteContext";
import { DueDiligenceTab } from "@/shared/components/PoolDetails/DueDiligence";
import { useCanCreateIOIQuery } from "@/services/auth/permissions";
import { ExpressInterestModal } from "@/routes/buy/-internal/expressInterestModal";
import { BuyStatusBadge } from "@/routes/buy/-internal/InterestedPoolsTab";

export const Route = createFileRoute("/buy/$poolId")({
  component: RouteComponent,
  validateSearch: z.object({
    tab: tabSchema.catch("tearsheet"),
  }),
});

function RouteComponent() {
  const { poolId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const { t } = useTranslation("buy");
  const { browseSearch } = useBuyRouteContext();
  const { institutionId } = useRouteContext({ from: "__root__" });

  // TODO: data date should be grabbed on the report service
  const dataDate = "2026-08-31";

  //TODO: Permissions check on what pool data is exposed
  const {
    data: poolData,
    isLoading,
    isPending,
  } = useGetPoolForBuyPageQuery({ id: Number(poolId) });

  const { data: createIOI } = useCanCreateIOIQuery();
  const [isInterestModalOpen, setIsInterestModalOpen] = useState(false);

  const poolDetails = useMemo(() => {
    if (!poolData?.pool) return {};

    const pool = poolData.pool;

    const poolBalance = pool.balance;

    // Falls back to 10, not 0, to mirror the backend's actual default for a missing value.
    const sellerRetentionDecimal = (pool.listing?.sellerRetentionPercent ?? 10) / 100;
    const availableBalance = poolBalance * (1 - sellerRetentionDecimal);

    return {
      id: pool.id,
      externalPoolName: pool.poolNumber ?? "",
      listedDate: pool.listing?.listingDate ?? pool.createdDate,
      sellerName: pool.client?.companyName ?? "Unknown",
      poolType: pool.poolType ?? null,
      recourseType: pool.listing?.recourseType ?? null,
      poolBalance,
      availableBalance,
      price: pool.listing?.listingPercentage ?? 0,
      grossYield: pool.grossYield ?? null,
      settlementType: pool.listing?.settlementType ?? null,
      servicingType: pool.listing?.servicingType ?? null,
      servicingFee: pool.servicingFeePercentage ?? null,
      totalLoans: pool.loanCount ?? 0,
      carveout: pool.listing?.carveOutPermitted ?? false,
      dueDiligence: pool.listing?.dueDiligencePeriod ?? null,
      dueDiligenceDuration: pool.listing?.dueDiligencePeriodDays ?? undefined,
      portfolioId: pool.loanPortfolioId!,
      clientId: pool.clientId!,
      participationPercent: 1 - sellerRetentionDecimal,
      status: pool.institutionLetterOfIntent?.status,
    };
  }, [poolData]);

  const formattedDate =
    isPending || !poolDetails.listedDate
      ? ""
      : new Date(poolDetails.listedDate).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
          timeZone: "UTC",
        });

  const handleTabChange = (_: React.SyntheticEvent, value: TabValue) => {
    navigate({
      from: Route.fullPath,
      search: { tab: value },
    });
  };

  const hasExpressedInterest =
    poolData?.pool?.institutionLetterOfIntent?.status === LetterOfIntentStatus.Pending;

  const isUnderLoi =
    poolData?.pool?.institutionLetterOfIntent?.status === LetterOfIntentStatus.Signed;

  return (
    <PageContentContainer>
      {isPending ? (
        <LoadingPageComponent />
      ) : !poolDetails.id ? (
        <PaxErrorPage
          header={t("poolDetail.poolNotFound")}
          subtext=""
          actionLabel={t("poolDetail.returnToPoolList")}
          onAction={() => navigate({ to: "/buy", search: browseSearch })}
        />
      ) : (
        <Fragment>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ mb: 2 }}
          >
            <Box display={"flex"} gap={2} alignItems={"center"}>
              <Box>
                <Typography variant="h6">{poolDetails.externalPoolName}</Typography>
                <Typography variant="body1">
                  {t("poolDetail.listed")} {formattedDate}
                </Typography>
              </Box>
              <BuyStatusBadge status={poolDetails.status} />
            </Box>
            <PaxTooltip
              title={
                !createIOI
                  ? t("poolDetail.expressInterestPermissionTooltip")
                  : hasExpressedInterest
                    ? t("poolDetail.expressInterestActionAlreadyCompleted")
                    : null
              }
            >
              <span>
                <Button
                  variant="contained"
                  sx={{ borderRadius: "8px", textTransform: "none" }}
                  disabled={createIOI && !hasExpressedInterest && !isUnderLoi ? !createIOI : true} // button should be disabled if query errors
                  onClick={() => setIsInterestModalOpen(true)}
                >
                  {t("poolDetail.expressInterest")}
                </Button>
              </span>
            </PaxTooltip>
          </Stack>

          <Tabs
            value={tab}
            onChange={handleTabChange}
            aria-label={t("poolDetail.detailTabs")}
            sx={{ "& .MuiTab-root": { textTransform: "none" } }}
          >
            <PaxTab label={t("poolDetail.tearSheet")} value="tearsheet" />
            <PaxTab label={t("poolDetail.analytics")} value="analytics" />
            <PaxTab
              label={`${t("poolDetail.loanList")} (${poolDetails.totalLoans})`}
              value="loan-list"
            />
            <PaxTab label={t("poolDetail.sellerInstitution")} value="seller-institution" />
            <PaxTab label={t("poolDetail.dueDiligence")} value="due-diligence" />
          </Tabs>
          <Divider sx={{ mb: 4 }} />

          <PaxTabPanel value={tab} index="tearsheet">
            <TearSheet
              poolBalance={poolDetails.poolBalance ?? 0}
              totalLoans={poolDetails.totalLoans ?? 0}
              amountAvailable={poolDetails.availableBalance ?? 0}
              grossYield={poolDetails.grossYield ?? null}
              assetClass={poolDetails.poolType}
              // newUsedString={"-"}
              settlementType={poolDetails.settlementType}
              recourseType={poolDetails.recourseType}
              price={poolDetails.price}
              // amortizationType={"-"}
              diligencePeriodType={poolDetails.dueDiligence}
              diligenceDuration={poolDetails.dueDiligenceDuration}
              carveOut={poolDetails.carveout}
              servicingType={poolDetails.servicingType}
              servicingFee={poolDetails.servicingFee ?? 0}
            />
          </PaxTabPanel>
          <PaxTabPanel value={tab} index="analytics">
            <PoolAnalytics
              dataDate={dataDate}
              poolId={poolDetails.id}
              portfolioId={poolDetails.portfolioId}
              institutionId={institutionId}
              isPublic
            />
          </PaxTabPanel>
          <PaxTabPanel value={tab} index="loan-list">
            <PoolLoanList
              poolId={poolDetails.id}
              totalLoans={poolDetails.totalLoans}
              institutionId={institutionId}
            />
          </PaxTabPanel>
          <PaxTabPanel value={tab} index="seller-institution">
            <InstitutionProfile institutionId={poolDetails.clientId} poolId={poolDetails.id} />
          </PaxTabPanel>
          <PaxTabPanel value={tab} index="due-diligence">
            <DueDiligenceTab
              hasExpressedInterest={hasExpressedInterest}
              isUnderLoi={isUnderLoi}
              poolId={poolId}
              openInterestModal={() => setIsInterestModalOpen(true)}
              canExpressInterest={createIOI ?? false}
            />
          </PaxTabPanel>
          <ExpressInterestModal
            key={`interest-modal-${isInterestModalOpen}`}
            onClose={() => setIsInterestModalOpen(false)}
            open={isInterestModalOpen}
            poolBalance={poolDetails.poolBalance}
            participationPercent={poolDetails.participationPercent}
            loanCount={poolDetails.totalLoans}
            askingPrice={poolDetails.price}
            poolName={poolDetails.externalPoolName}
            poolId={poolDetails.id}
            sellerName={poolDetails.sellerName}
          />
        </Fragment>
      )}
    </PageContentContainer>
  );
}
